import { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, ScaleControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const CLIMB_MAP_COLORS = {
  'B-C4': '#ffe066',
  'B-C3': '#ffa020',
  'B-C2': '#e05000',
  'B-C1': '#c01010',
  'B-HC': '#7a0000',
};

const DESCENT_MAP_COLORS = {
  'B-D4':  '#a0d0ff',
  'B-D3':  '#4090e0',
  'B-D2':  '#1060b0',
  'B-D1':  '#003880',
  'B-DHC': '#001840',
};

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) map.fitBounds(bounds, { padding: [20, 20] });
  }, [bounds, map]);
  return null;
}

function ZoomPointsLayer({ points }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [bounds, setBounds] = useState(map.getBounds());

  useMapEvents({
    zoomend: () => { setZoom(map.getZoom()); setBounds(map.getBounds()); },
    moveend: () => setBounds(map.getBounds()),
  });

  if (zoom < 15 || !points || points.length === 0) return null;

  return points
    .filter(pt => pt.lat && pt.lon && bounds.contains([pt.lat, pt.lon]))
    .map((pt, idx) => (
      <CircleMarker
        key={idx}
        center={[pt.lat, pt.lon]}
        radius={3}
        pathOptions={{ color: '#fff', fillColor: '#90caf9', fillOpacity: 0.9, weight: 1 }}
      >
        <Popup>
          <div style={{ fontSize: 12 }}>
            <div><b>{(pt.cumulative_distance / 1000).toFixed(2)} km</b></div>
            <div>Elevation: {pt.elevation?.toFixed(0)} m</div>
          </div>
        </Popup>
      </CircleMarker>
    ));
}

const BikeRouteMap = ({ chunks, title, climbHighlights = [], descentHighlights = [], segmentHighlight = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  // Flatten all GPS points from chunks into a single ordered array
  const allPoints = [];
  if (chunks) {
    chunks.forEach(chunk => {
      if (!chunk.points || !Array.isArray(chunk.points)) return;
      chunk.points.forEach(pt => {
        if (pt.lat && pt.lon) allPoints.push(pt);
      });
    });
  }

  const routeCoords = allPoints.map(pt => [pt.lat, pt.lon]);

  if (routeCoords.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: colors.grey[800], borderRadius: '8px' }}>
        <Typography color={colors.grey[300]}>
          {t('noGpsData', 'No GPS data available for map')}
        </Typography>
      </Box>
    );
  }

  const center = [
    routeCoords.reduce((s, c) => s + c[0], 0) / routeCoords.length,
    routeCoords.reduce((s, c) => s + c[1], 0) / routeCoords.length,
  ];

  const startCoord = routeCoords[0];
  const finishCoord = routeCoords[routeCoords.length - 1];

  const startIcon = L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;background:#4caf50;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });

  const finishIcon = L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;background:#f44336;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });

  return (
    <Box>
      {title && (
        <Typography variant="h6" color={colors.grey[100]} mb={2}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          height: 400,
          borderRadius: '8px',
          overflow: 'hidden',
          '& .leaflet-container': { height: '100%', backgroundColor: colors.grey[900] },
        }}
      >
        <MapContainer center={center} zoom={13} maxZoom={19} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            maxZoom={19}
          />

          <Polyline
            positions={routeCoords}
            pathOptions={{ color: colors.blueAccent[400], weight: 4, opacity: 0.85 }}
          />

          <Marker position={startCoord} icon={startIcon}>
            <Popup>{t('start', 'Start')}</Popup>
          </Marker>
          <Marker position={finishCoord} icon={finishIcon}>
            <Popup>{t('finish', 'Finish')}</Popup>
          </Marker>

          {climbHighlights && climbHighlights.map((climb, i) => {
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= climb.start_distance_m && d <= climb.end_distance_m && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            const color = CLIMB_MAP_COLORS[climb.category] || CLIMB_MAP_COLORS['B-C4'];
            return (
              <Polyline key={`climb-hl-${i}`} positions={coords} pathOptions={{ color, weight: 6, opacity: 0.8 }} />
            );
          })}

          {descentHighlights && descentHighlights.map((descent, i) => {
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= descent.start_distance_m && d <= descent.end_distance_m && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            const color = DESCENT_MAP_COLORS[descent.category] || DESCENT_MAP_COLORS['B-D4'];
            return (
              <Polyline key={`descent-hl-${i}`} positions={coords} pathOptions={{ color, weight: 6, opacity: 0.8 }} />
            );
          })}

          {segmentHighlight && (() => {
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= segmentHighlight.start_distance_m && d <= segmentHighlight.end_distance_m && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            return (
              <Polyline
                key="segment-hl"
                positions={coords}
                pathOptions={{ color: '#ffeb3b', weight: 7, opacity: 0.92 }}
              />
            );
          })()}

          <ZoomPointsLayer points={allPoints} />
          <FitBounds bounds={routeCoords} />
          <ScaleControl position="bottomleft" />
        </MapContainer>
      </Box>
    </Box>
  );
};

export default BikeRouteMap;
